/**
 * Cart toast — shows why a cart mutation failed.
 *
 * Listens for `cart_error` on the document so any surface (quick add on a
 * listing, the quick add drawer, a product form) can report a failure without
 * owning its own message UI. Shopify returns a human-readable reason on a 422,
 * so that text is preferred over the generic fallback.
 */
(() => {
  const TAG = 'cart-toast';
  const ERROR_EVENT = 'cart_error';
  const EXIT_DURATION = 300;

  class CartToast extends HTMLElement {
    #hideTimer = 0;
    #removeTimer = 0;

    connectedCallback() {
      document.addEventListener(ERROR_EVENT, this.#onCartError);
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      document.removeEventListener(ERROR_EVENT, this.#onCartError);
      this.removeEventListener('click', this.#onClick);
      window.clearTimeout(this.#hideTimer);
      window.clearTimeout(this.#removeTimer);
    }

    show(message) {
      const text = String(message || '').trim() || this.dataset.fallbackMessage || '';

      if (!text) return;

      const target = this.querySelector('[data-cart-toast-message]');

      if (!(target instanceof HTMLElement)) return;

      window.clearTimeout(this.#hideTimer);
      window.clearTimeout(this.#removeTimer);

      target.textContent = text;
      this.hidden = false;

      // The panel starts translated and transparent, so the open attribute has
      // to land on a later frame for the transition to run at all.
      requestAnimationFrame(() => {
        this.setAttribute('data-open', 'true');
      });

      const duration = Number(this.dataset.duration) || 8000;

      this.#hideTimer = window.setTimeout(() => this.hide(), duration);
    }

    hide() {
      window.clearTimeout(this.#hideTimer);

      if (!this.hasAttribute('data-open')) return;

      this.removeAttribute('data-open');

      this.#removeTimer = window.setTimeout(() => {
        this.hidden = true;
      }, EXIT_DURATION);
    }

    #onCartError = (event) => {
      this.show(event.detail?.message);
    };

    #onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;

      if (!target?.closest('[data-cart-toast-dismiss]')) return;

      event.preventDefault();
      this.hide();
    };
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, CartToast);
  }
})();
